import type { CSSProperties } from "react";
import type { Integration } from "../integrationTypes";
import { CATEGORY_LABELS } from "../integrationMockData";
import {
  groupByCategory,
  getConnected,
  getAvailable,
} from "../integrationUtils";
import { IntegrationCard } from "./IntegrationCard";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";

/**
 * The responsive grid of integration cards for the catalog.
 *
 * Mobile-first columns via useBreakpoint():
 *   mobile (<640): 1 column
 *   tablet (640-1023): 2 columns
 *   desktop (1024+): 3 columns
 *
 * Pulls connected integrations into their own section at the top when
 * any exist, then groups the remainder by category in CATEGORY_ORDER.
 * When nothing matches the current filter, renders a short empty-filter
 * message so the user knows the catalog isn't broken.
 *
 * Takes an already-filtered list; search and category filtering happen
 * upstream in the page composer via IntegrationToolbar. Grid is a pure
 * presenter: data in, cards out.
 */

interface IntegrationGridProps {
  integrations: Integration[];
  onConnect: (integration: Integration) => void;
  onLearnMore?: (integration: Integration) => void;
}

const PALETTE = {
  ink3: "#3A4A52",
  ink4: "#6B7684",
};

export function IntegrationGrid({
  integrations,
  onConnect,
  onLearnMore,
}: IntegrationGridProps) {
  const { isMobile, isTablet } = useBreakpoint();
  const columns = isMobile ? 1 : isTablet ? 2 : 3;

  const gridStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap: isMobile ? 12 : 16,
  };

  const connected = getConnected(integrations);
  const available = getAvailable(integrations);
  const grouped = groupByCategory(available);

  if (integrations.length === 0) {
    return (
      <div style={emptyFilterStyle}>
        No integrations match your filter. Try clearing the search or
        category.
      </div>
    );
  }

  return (
    <div>
      {connected.length > 0 && (
        <section style={sectionStyle}>
          <h2 style={sectionLabelStyle}>Connected · {connected.length}</h2>
          <div style={gridStyle}>
            {connected.map((integration) => (
              <IntegrationCard
                key={integration.id}
                integration={integration}
                onPrimaryClick={onConnect}
                onLearnMore={onLearnMore}
              />
            ))}
          </div>
        </section>
      )}

      {grouped.map(({ category, items }) => (
        <section key={category} style={sectionStyle}>
          <h2 style={sectionLabelStyle}>
            {connected.length > 0
              ? `Available ${CATEGORY_LABELS[category].toLowerCase()}`
              : CATEGORY_LABELS[category]}
          </h2>
          <div style={gridStyle}>
            {items.map((integration) => (
              <IntegrationCard
                key={integration.id}
                integration={integration}
                onPrimaryClick={onConnect}
                onLearnMore={onLearnMore}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

const sectionStyle: CSSProperties = {
  marginBottom: 32,
};

const sectionLabelStyle: CSSProperties = {
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: PALETTE.ink3,
  fontWeight: 700,
  margin: "0 0 14px",
};

const emptyFilterStyle: CSSProperties = {
  padding: "60px 20px",
  textAlign: "center",
  color: PALETTE.ink4,
  fontSize: 13,
};